import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import FormDropdown from '@wix/da-ds/pkg/Dropdown/Preset/FormDropdown';
import CloseIcon from '@wix/da-ds/pkg/Icons/16x16/Close';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import DollarPriceInput from '@wix/da-shared-react/pkg/DollarPriceInput';

import s from './RateRow.scss';

export interface Props {
  index: number;
  defaultDestinationKey: string;
  defaultPriceUsd: string;
  availableOptions: { value: string; label: string }[];
  canRemove: boolean;
  onRemove: () => void;
}

const RateRow: React.FC<Props> = ({
  index,
  defaultDestinationKey,
  defaultPriceUsd,
  availableOptions,
  canRemove,
  onRemove,
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const rateErrors = (errors.rates as any)?.[index];
  const destinationError = rateErrors?.destinationKey?.message as string | undefined;
  const priceError = rateErrors?.priceUsd?.message as string | undefined;

  return (
    <div className={s['root']}>
      <div className={s['fields']}>
        <Controller
          control={control}
          name={`rates.${index}.destinationKey`}
          defaultValue={defaultDestinationKey}
          render={({ value, onChange }) => (
            <div className={s['destination-field']}>
              <FormDropdown
                items={availableOptions}
                selectedValue={value || null}
                onSelect={v => onChange(v as string)}
                placeholder={t('manageShippingProfiles.editor.destination.placeholder')}
                className={s['destination']}
              />
              {destinationError && (
                <div className={s['destination-error']}>
                  <InputErrorMessage>{destinationError}</InputErrorMessage>
                </div>
              )}
            </div>
          )}
        />
        <Controller
          control={control}
          name={`rates.${index}.priceUsd`}
          defaultValue={parseInt(defaultPriceUsd)}
          render={({ value, onChange }) => (
            <DollarPriceInput
              label={t('manageShippingProfiles.editor.price.ariaLabel')}
              name={`rates.${index}.priceUsd`}
              value={value}
              onChange={newValue => onChange(newValue)}
              error={priceError}
              isLabelHidden
              noBottomSpacing
              hidePointsInfo
              forceInteger
              min={0}
              className={s['price-field']}
            />
          )}
        />
        {canRemove && (
          <IconButton
            size="small"
            variant="main"
            className={s['remove-button']}
            icon={CloseIcon}
            onClick={onRemove}
            aria-label={t('manageShippingProfiles.editor.removeRate.ariaLabel')}
          />
        )}
      </div>
    </div>
  );
};
RateRow.displayName = 'RateRow';

export default RateRow;
